import md5 from 'md5';

import externalConfiguration from '../external-configuration';
import { avoidCache, patchM3u8Url, IE11_safeFrameElement } from './utils';

/**
 *  Esta es una funcion auxiliar usada por legacyPlayerReadData para simplificar la busqueda en el XML
 */
function getXMLTagAttr( xml, tag, attribute = undefined, required = false) {
  let ret = "";
  let val = xml.getElementsByTagName(tag);
  if (val.length > 0) {
    if (attribute !== undefined) {
      ret = val[0].getAttribute(attribute);
    } else {
      if (val[0].childNodes.length > 0) {
        ret = val[0].childNodes[0].nodeValue;
      } else if (required) {
        console.error("El tag ", tag, " no contiene childNodes, por lo que no se puede leer su nodeValue, es un valor necesario asi que el proceso no puede continuar; XML: ", xml);
      } else {
        // console.info("El tag ", tag, " no contiene childNodes, por lo que no se puede leer su nodeValue, es un valor preescindible por lo que se le asigna cadena vacia; XML: ", xml);
      }
    }
  } else if (required) {
    console.error("El tag ", tag, " no se ha encontrado en el xml, es un valor necesario asi que el proceso no puede continuar; XML: ", xml);
  } else {
    // console.info("El tag ", tag, " no se ha encontrado en el xml, es un valor preescindible por lo que se le asigna cadena vacia; XML: ", xml);
  }
  return ret;
}

/**
 *  El contenido del campo data de la informacion del player viene encriptado en base64, y en un XML al que se le ha
 *  concatenado informacion extra en formato de estilo similar, pero no parseable, esta funcion corrige eso, y traduce
 *  la informacion al nuevo modelo de datos.
 *
 *  La sub-estructura data.playlist solo contiene informacion util en los player Live
 *
 *  La sub-estructura data.debug existe para escalar valores tal como se recivieron, permitiendo revisarlos a posteriori.
 */
function legacyPlayerReadData( player) {
  let data = atob( player.data.replace( /%2B/g, '+')).split('<@@@>');
  data.user = player.user;
  let parser = new DOMParser();   // Esto no es compatible con Internet Explorer 8 o anterior
  data.xml = parser.parseFromString( data[0], "application/xml");
  let color = parseInt( getXMLTagAttr( data.xml, "controls", "color").replace('0x', ''), 16);
  let alpha = getXMLTagAttr( data.xml, "controls", "alpha");
  data.customProps = {
    autostart: getXMLTagAttr( data.xml, 'settings', 'autoplay'),
    skin: {        // Esto deberia parametrizarse en admin player, y referenciar skins auto-alojados
    	name: 'glow',
    	active: '#666666',
    	inactive: '#aaaaaa',
    	background: 'rgba(' + ((color >> 16) & 255) + ', ' + ((color >> 8) & 255) + ', ' + (color & 255) + ', ' + alpha + ')',
    },
  };
  if( getXMLTagAttr( data.xml, 'share', 'visible') === 'true') {   // Este bloque construye la seccion de compartir si es necesario
    let code = IE11_safeFrameElement();
    if( !code) {
      code = '<iframe src="' + document.URL + '"></iframe>';
    }
    let networks = data.xml.getElementsByTagName( 'network');
    let sites = [];
    for (let i = 0; i < networks.length; i++) {
      sites[i] = networks[i].getAttribute('name');
      if (sites[i] === 'Google +') {
        sites[i] = 'googleplus';
      } else if (sites[i] === 'Mail') {
        sites[i] = 'email';
      } else {
        sites[i] = sites[i].toLowerCase();
      }
    }
    data.customProps.sharing = {
      code: code,
      link: /src=["']([^"']*)["']/.exec(code)[1],
      sites: sites,
    };
  }
  // if( getXMLTagAttr(data.xml, "logo", "visible" === 'true')) {  // Este bloque construye la seccion del logo si es necesario
  let src = getXMLTagAttr(data.xml, "logo", "src");
  if( src !== '') {
    const positions = [ 'top-left', 'top-left', 'top-right', 'bottom-left', 'top-right', 'bottom-left', 'bottom-right', 'bottom-right'];
    data.customProps.logo = {
      file: avoidCache( src),
      position: positions[ parseInt( getXMLTagAttr(data.xml, "logo", "pos"), 10)],
    };
    let link = getXMLTagAttr(data.xml, "logo", "url");
    if( link !== '') {
      data.customProps.logo.link = avoidCache( link);
    }
  }
  data.playlist = [{  // Esta playlist sera el elemento reproducible, si es live, si es vod, solo el campo 'adschedule' contiene informacion valida, si se va a usar para reproducir una playlist incluso ese sera sobreescrito
    image: avoidCache( player.prevPic),
    file: patchM3u8Url( player.src),
  }];
	let pre = getXMLTagAttr( data.xml, "videoAds", "pre_roll");    // Este bloque construye la seccion de publicidad si es necesario
	let mid = getXMLTagAttr( data.xml, "videoAds", "mid_roll");
	let post = getXMLTagAttr( data.xml, "videoAds", "pos_roll");
	let pre_jump = getXMLTagAttr( data.xml, "videoAds", "jmPnt_Pre");
	let mid_jump = getXMLTagAttr( data.xml, "videoAds", "jmPnt_Mid");
  let advertising = {
    client: 'vast',
  };
  let adschedule = {}
	if( pre !== '' && pre !== 'false') {
    adschedule.adbreak1 = {
      offset: 'pre',
      tag: avoidCache( pre),
      skipoffset: pre_jump,
    };
	}
	if( mid !== '' && mid !== 'false') {
    adschedule.adbreak2 = {
      offset: '50%',
      tag: avoidCache( mid),
      skipoffset: mid_jump,
    };
	}
  if( post !== '' && post !== 'false') {
    adschedule.adbreak3 = {
      offset: "post",
      tag: avoidCache( post),
    };
  }
  if( Object.keys( adschedule).length !== 0) {
    data.customProps.advertising = advertising;
    data.playlist[0].adschedule = adschedule;
  }
  data.debug = {
    imageFromPlayerPrevPic: player.prevPic,
    imageFromPlayerXMLVidPic: getXMLTagAttr( data.xml, 'vidPic'),
    fileFromPlayerSrc: player.src,
    fileFromPlayerSrcMobile: player.srcMobile,
  };
  data.basePlayerOptions = { download: getXMLTagAttr( data.xml, 'share', 'download') };
  return data;
}

/**
 *  Esta funcion uniformaiza la informacion de reestricciones de reproduccion, leida de config en players Live, y de player en players Vod.
 */
function legacyRestrictionsReadData( settings) {    // REVIEW
  // Para garantizar compatibilidad es posible que sea necesario comprobar el valor de blkPic, para verificar si esta no existe, si es un archivo, o una url; y actuar en consecuencia.
  // Asi mismo tambien puede ser necesario comprobar el tipo de domainsBlocked y countriesBlocked, y ajustarlos.
  return {
    playbackRestrictions: {
      domainBlockActive: !settings.canDisplayContent,
      domainsBlocked: settings.domainsBlocked,
      countryBlockActive: settings.isGeoBLocked,
      countriesBlocked: settings.geoBlocking,
      displayImageWhenBlock: externalConfiguration.defaultBlockImage,
      // displayImageWhenBlock: externalConfiguration.defaultBlockImagePath + settings.blkPic,
    },
  };
}

/**
 *  Esta funcion carga toda la informacion de un player live segun la estructura de la plataforma 'vieja' (en produccion a 09-2017)
 */
function legacyLoadLive( playerId) { // REVIEW
  let promiseOfConfig = fetch( avoidCache( '/definition/config/' + md5('definition-config' + playerId) + '.json')).then( (response) => { return response.json();});
  let promiseOfPlayer = fetch( avoidCache( '/definition/players/' + md5('definition-' + playerId) + '.json')).then( (response) => { return response.json();});
  return promiseOfConfig.then( (config) => {
    let restrictionSettings = legacyRestrictionsReadData( config);
    return promiseOfPlayer.then( (player) => {
      let settings = legacyPlayerReadData( player);
      return { ...restrictionSettings, ...settings};
    });
  });
}

/**
 *  Esta funcion sobreescribe la informacion del origen de media de settings cargada desde el player, por la cargada desde las opciones del video; solo para Vod
 */
function legacyVideoReadData( settings, video) {
  settings.playlist[0].file = patchM3u8Url( video.srcMobile);
  settings.playlist[0].image = avoidCache( video.prevPic);
  let userDataPath = externalConfiguration.cdnProgressiveDomain + '/userdata/' + video.user;
  settings.playlist[0].tracks = [{
			file: avoidCache( userDataPath + '/storyboard/' + video.videoId + '.vtt'),
			kind: "thumbnails",
	}];
  if( video.chapters === 1 || video.chapters === '1') {
    settings.playlist[0].tracks.push({
        file: avoidCache(  '/definition/vod/chapters/' + video.user + '/' + video.videoId + '.vtt'),
        kind: "chapters",
    });
  }
  if( video.subtitles) video.subtitles.forEach( (subtitle) => {
    settings.playlist[0].tracks.push( {
      file: avoidCache( '/definition/vod/subtitles/' + video.user + '/' + video.videoId + '/' + subtitle + '.vtt'),
      label: subtitle,
      kind: "captions",
    });
  });
  settings.customProps.related = {
    'file': avoidCache( '/definition/vod/relatedToVideo/' + video.user + '/' + video.videoId + '.json'),
  };
  settings.debug.fileFromVideoSrc = video.src;
  settings.debug.fileFromVideoSrcMobile = video.srcMobile;
  return settings;
}

/**
 *  Esta funcion carga toda la informacion de un video, segun la estructura de la plataforma 'vieja' (en produccion a 04-2018)
 */
function legacyLoadVideo( user, videoId, promiseOfSettings) {
  let promiseOfVideo = fetch( avoidCache( '/definition/vod/videos/' + user + '/' + md5('definition-config-vod-' + videoId) + '.json')).then( (response) => { return response.json();});
  let promiseOfVideoExt = fetch( avoidCache( '/definition/vod/extra/' + user + '/' + videoId + '.json')).then( (response) => { return response.json();});
  return promiseOfSettings.then( (settings) => {
    return promiseOfVideo.then( (video) => {
      return promiseOfVideoExt.then( (videoExt) => {
        video.subtitles = videoExt.subtitles;
        video.chapters = videoExt.chapters;
        settings = legacyVideoReadData( settings, video);
        settings.playbackRestrictions.publishDate = videoExt.publishDate;
        settings.playbackRestrictions.unpublishDate = videoExt.unpublishDate;
        settings.playbackRestrictions.publishTimeStamp = videoExt.publishTimeStamp;
        settings.playbackRestrictions.unpublishTimeStamp = videoExt.unpublishTimeStamp;
        settings.playbackRestrictions.state = videoExt.state;
        return settings;
      }).catch( (error) => {
        settings = legacyVideoReadData( settings, video);
        return settings;
      });
    });
  });
}


/**
 *  Esta funcion carga toda la informacion de un player vod, y el video que ha de reproducir, segun la estructura de la plataforma 'vieja' (en produccion a 09-2017)
 */
function legacyLoadVod( playerId, videoId) {
  let promiseOfPlayer = fetch( avoidCache( '/definition/vod/players/' + md5('definition-config-player-' + playerId) + '.json')).then( (response) => { return response.json();});
  return promiseOfPlayer.then( (player) => {
    let promiseOfSettings = new Promise( (resolve,reject) => setTimeout( () => {
      resolve( { ...legacyRestrictionsReadData( player), ...legacyPlayerReadData( player)});
    },0));
    return legacyLoadVideo( player.user, videoId, promiseOfSettings);
  });
}

////////// DEPRECATED, se ha decidido no dar soporte a este modelo de playlist en el nuevo player
/**
 *  Esta funcion carga toda la informacion de una playlist segun la estructura de la plataforma 'vieja' (en produccion a 09-2017)
 *  pendiente de que se decida si se va a completar la implementacion, ya que el nuevo concepto de playlist no es compatible con el anterior
 */
// function legacyLoadPlaylist( playlistId) { // TODO
//   let promiseOfPlaylist = fetch( avoidCache( '/definition/playlist/embed/' + playlistId + '.json')).then( (response) => { return response.json();});
//   return promiseOfPlaylist.then( (playlistSettings) => {
//     let user = 'faf';                                                 // Esto debe leerse de la informacion de la playlist
//     let subfamilyId = '34';                                           // Esto debe leerse de la informacion de la playlist
//     let programmationId = 'e1b82f70-d531-42aa-a016-4b2dc92560a3';     // Esto debe leerse de la informacion de la playlist
//     let promiseOfFamilies = fetch( avoidCache( '/definition/playlist/' + user + '/families.json')).then( (response) => { return response.json();});
//     let promiseOfSubfamily = fetch( avoidCache( '/definition/playlist/' + user + '/subfamily-' + subfamilyId + '.json' )).then( (response) => { return response.json();});
//     let promiseOfProgrammation = fetch( avoidCache( '/definition/programmation/' + user + '/' + programmationId + '.json' )).then( (response) => { return response.json();});
//     return promiseOfFamilies.then( (families) => {
//       return promiseOfSubfamily.then( (subfamily) => {
//         return promiseOfProgrammation.then( (programmation) => {
//           return { customProps: {}, playlist: [{}], playlistSttings: playlistSettings, families: families, subfamily: subfamily, programmation: programmation};
//         });
//       });
//     });
//   });
// }

/**
 *  Esta funcion carga toda la informacion de un iframe segun la estructura de la plataforma 'vieja' (en produccion a 09-2017)
 *  y traduce dichos datos a la estructura de informacion que va a usar la plataforma nueva, en desarrollo
 */
function legacyToState( id1, id2) {
  let visualizerType = 'Unknown';
  // if( id1 === 'playlist') {   // DEPRECATED, se ha decidido no dar soporte a este modelo de playlist en el nuevo player
  //   visualizerType = 'Playlist';
  // } else
  if( /^[0-9a-zA-Z]{10}$/.test( id1)) {
    visualizerType = 'Vod';
  } else if( /^[0-9a-z]{8}$/.test( id1)) {
    visualizerType = 'Live';
  }
  let settings = {
    legacy: true,
    visualizerType: visualizerType,
  };
  let promiseOfSettings;
  switch( visualizerType) {
    case 'Live':
      promiseOfSettings = legacyLoadLive( id1);
      break;
    case 'Vod':
      promiseOfSettings = legacyLoadVod( id1, id2);
      break;
    // case 'Playlist':   // DEPRECATED, se ha decidido no dar soporte a este modelo de playlist en el nuevo player
    //   promiseOfSettings = legacyLoadPlaylist( id2);
    //   break;
    default:
      promiseOfSettings = new Promise( (resolve) => {
        resolve( { customProps: {}, playlist: [{}] });
      });
  };
  let commonCustomProps = {
    hlshtml: true,
    primary: "flash",
    abouttext: externalConfiguration.defaultPlayerAboutText,
    aboutlink: externalConfiguration.defaultPlayerAboutLink,
    width: window.innerWidth,
    height: window.innerHeight,
  };
  settings.visualizerOptions = {
    onResize: ( state) => { state.customProps.width = window.innerWidth; state.customProps.height = window.innerHeight; return state;},
  }
  return promiseOfSettings.then( (typeSettings) => {
    Object.assign( settings, typeSettings);
    Object.assign( settings.customProps, commonCustomProps);
    return settings;
  });
};

export default legacyToState;
export { legacyLoadVideo, legacyLoadVod };



// WEBPACK FOOTER //
// ./src/helpers/legacy-to-state.js